import React, { useState, useEffect } from 'react';
import { queryLogsByRequestId } from './api_utils';

export const LogsViewer = ({ jobId, functionName, requestId, doctorName }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchHours, setSearchHours] = useState(24);
  const [logLimit, setLogLimit] = useState(1000);

  useEffect(() => {
    fetchLogs();
  }, [requestId, functionName]);

  const fetchLogs = async () => {
    if (!requestId || !functionName) {
      setError('Missing request ID or function name');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await queryLogsByRequestId(requestId, functionName, searchHours, logLimit);
      
      if (result.success) {
        setLogs(result.logs);
      } else {
        setError(result.error || 'Failed to fetch logs');
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError(`Error fetching logs: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchLogs();
  };

  const handleExportLogs = () => {
    if (logs.length === 0) {
      alert('No logs to export');
      return;
    }

    const exportData = {
      jobId,
      functionName,
      requestId,
      doctorName,
      searchHours,
      logLimit,
      fetchedAt: new Date().toISOString(),
      totalLogs: logs.length,
      logs: logs
    };

    const exportText = JSON.stringify(exportData, null, 2);
    const blob = new Blob([exportText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs_${jobId}_${requestId}_${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const getLogLevelColor = (message) => {
    if (message.includes('ERROR') || message.includes('Error')) return 'text-red-600';
    if (message.includes('WARN') || message.includes('Warning')) return 'text-yellow-600';
    if (message.includes('INFO')) return 'text-blue-600';
    if (message.includes('DEBUG')) return 'text-gray-600';
    return 'text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900">CloudWatch Logs Viewer</h1>
            <div className="flex gap-3">
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>

          {/* Job Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-semibold text-gray-600">Job ID:</span>
              <p className="text-gray-900 font-mono break-all">{jobId}</p>
            </div>
            <div>
              <span className="font-semibold text-gray-600">Function:</span>
              <p className="text-gray-900">{functionName}</p>
            </div>
            <div>
              <span className="font-semibold text-gray-600">Request ID:</span>
              <p className="text-gray-900 font-mono break-all">{requestId}</p>
            </div>
            <div>
              <span className="font-semibold text-gray-600">Doctor:</span>
              <p className="text-gray-900">{doctorName}</p>
            </div>
          </div>

          {/* Search Controls */}
          <div className="flex items-center gap-4 mt-4 pt-4 border-t">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600">Search Hours:</label>
              <select
                value={searchHours}
                onChange={(e) => setSearchHours(parseInt(e.target.value))}
                className="border rounded px-2 py-1 text-sm"
              >
                <option value={1}>1 hour</option>
                <option value={6}>6 hours</option>
                <option value={24}>24 hours</option>
                <option value={72}>3 days</option>
                <option value={168}>1 week</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600">Limit:</label>
              <select
                value={logLimit}
                onChange={(e) => setLogLimit(parseInt(e.target.value))}
                className="border rounded px-2 py-1 text-sm"
              >
                <option value={50}>50 entries</option>
                <option value={100}>100 entries</option>
                <option value={500}>500 entries</option>
                <option value={1000}>1000 entries</option>
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm border p-8">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Getting logs...</h2>
              <p className="text-gray-600">
                Querying CloudWatch Logs for request ID: {requestId}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This may take a few seconds to complete
              </p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <div className="text-red-400 text-xl mr-3">⚠</div>
              <div>
                <h3 className="text-lg font-semibold text-red-800">Error Loading Logs</h3>
                <p className="text-red-700 mt-1">{error}</p>
                <button
                  onClick={handleRefresh}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Logs Display */}
        {!loading && !error && (
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-4 border-b bg-gray-50 rounded-t-lg">
              <h2 className="text-lg font-semibold text-gray-900">
                Log Entries {logs.length > 0 && `(${logs.length} found)`}
              </h2>
              {logs.length > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  Showing logs from the last {searchHours} hour{searchHours !== 1 ? 's' : ''}
                </p>
              )}
            </div>
            
            {logs.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-gray-400 text-4xl mb-4">📝</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Logs Found</h3>
                <p className="text-gray-600">
                  No log entries found for request ID: <code className="bg-gray-100 px-2 py-1 rounded">{requestId}</code>
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Try increasing the search time range or check if the request ID is correct.
                </p>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                {logs.map((log, index) => (
                  <div key={index} className="p-4 border-b hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="text-xs text-gray-500 font-mono whitespace-nowrap mt-1">
                        {formatTimestamp(log['@timestamp'])}
                      </div>
                      <div className="flex-1 min-w-0">
                        <pre className={`text-sm whitespace-pre-wrap break-words ${getLogLevelColor(log['@message'])}`}>
                          {log['@message']}
                        </pre>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

